/**
 * PATH: packages/kanban-host/src/allocation/useCreateBoardFromPicker.ts
 * FILE_ID: KANBANHOST-ALLOC-CREATEBOARD-001
 * PURPOSE: Shared "create a board straight from the allocate picker"
 *          behaviour. Wraps the same createBoard → (clone | provision
 *          lanes) → cache-invalidate flow the Kanban rail/gallery use,
 *          and hands the new board back as an `AllocateMenuBoard` so the
 *          picker can immediately drill into its lane step.
 * WHY:     Operator (2026-06-15): *"I want to create a new board to
 *          allocate here.. yet there is no option fixed underneath this
 *          drop down"*. The allocate dropdowns (AllocateChip /
 *          MoveToBoardPicker) only LISTED existing boards and told the
 *          operator to go create one in the Kanban app — a dead end mid
 *          allocation. Both surfaces now share THIS hook so the create
 *          flow stays identical (and identically tested) in both places.
 * FLOW:
 *   ┌────────────────────┐   ┌──────────────────────────┐   ┌───────────────────┐
 *   │ CreateBoardInput   │ → │ createBoard + clone/      │ → │ AllocateMenuBoard │
 *   │ (from dialog)      │   │ provisionLanes + refresh  │   │ (ready to drill)  │
 *   └────────────────────┘   └──────────────────────────┘   └───────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : buildBoardCreatePayload (snake_case payload +
 *     slug), getActiveLaneCount, Board / CreateBoardInput / KanbanApi types
 *   - @tanstack/react-query : invalidate the picker + rail board caches
 *   - ./useAllocateToKanbanMenuData : deriveSwatch + AllocateMenuBoard
 *     (so the returned board renders identically to a listed one)
 * RELATED:
 *   - packages/ui/src/kanban/components/KanbanBoardList.tsx (handleCreate —
 *     the canonical clone/provision flow this mirrors)
 *   - ./AllocateChip.tsx / ./MoveToBoardPicker.tsx (consumers)
 */

import { useCallback } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  buildBoardCreatePayload,
  clearProvisionedLanes,
  getActiveLaneCount,
  type Board,
  type CreateBoardInput,
  type KanbanApi,
} from '@quotech/ui/kanban'

import { deriveSwatch, type AllocateMenuBoard } from './useAllocateToKanbanMenuData'

/**
 * Pure I→O — turn a freshly created `Board` into the `AllocateMenuBoard`
 * shape the picker rows + lane drill-in expect. `laneCount` here is
 * best-effort from the create payload (the authoritative count comes
 * from the invalidated `['kanban-boards']` refetch); the immediate
 * drill-in uses `useKanbanBoardLanes` which fetches lanes directly, so
 * an under-count here never blocks allocation.
 */
export function toAllocateMenuBoard(board: Board): AllocateMenuBoard {
  return {
    id: board.id,
    name: board.name,
    swatch: deriveSwatch(board),
    laneCount: getActiveLaneCount(board),
    board,
  }
}

/**
 * Pure I→O — the cache keys to refresh after a board is created. The
 * allocate menu reads `['kanban-boards']`; the Kanban rail + gallery
 * read `['kanban','boards']`. We refresh BOTH so the new board appears
 * everywhere immediately instead of waiting for a stale-time expiry.
 */
export function buildBoardCreateInvalidationKeys(): Array<Array<string>> {
  return [['kanban-boards'], ['kanban', 'boards']]
}

/**
 * I→O core (no React) — create the board and bring it to a usable
 * state, mirroring KanbanBoardList.handleCreate:
 *   - clone path: copy the template board's non-archived lanes (+ theme
 *     when `cloneConfig !== false`); cards are NOT copied.
 *   - default path: provision the kind's canonical lanes (idempotent;
 *     a no-op for kinds without a canonical set, e.g. CUSTOM).
 * Clone/provision failures are non-fatal — the operator still gets the
 * board and can add lanes manually — so the allocate flow never hard
 * fails on a secondary step.
 */
export async function runBoardCreate(
  api: KanbanApi,
  input: CreateBoardInput,
): Promise<Board> {
  const created = await api.createBoard(buildBoardCreatePayload(input))

  if (input.cloneFromBoardId) {
    try {
      // BUG-2026-07-03-KANBAN-CLONE-LANES — strip the auto-provisioned default
      // lanes before mirroring the template's, so name collisions on the
      // (board, name) unique constraint don't silently keep the defaults.
      await clearProvisionedLanes(api, created.id)
      const templateLanes = await api.listLanes(input.cloneFromBoardId)
      const sortedLanes = templateLanes
        .filter((l) => !l.archived)
        .sort((a, b) => a.position - b.position)
      for (let i = 0; i < sortedLanes.length; i++) {
        const tl = sortedLanes[i]
        await api.createLane({
          board: created.id,
          name: tl.name,
          kind: tl.kind,
          color: tl.color,
          wip_limit: tl.wip_limit,
          position: i,
        } as never)
      }
      if (input.cloneConfig !== false) {
        const tmpl = await api.getBoard(input.cloneFromBoardId)
        if (tmpl.config_json) {
          await api.updateBoard(created.id, { config_json: tmpl.config_json } as never)
        }
      }
    } catch {
      // Non-fatal — empty-ish board is recoverable; operator adds lanes.
    }
  } else {
    try {
      await api.provisionLanes(created.id)
    } catch {
      // Non-fatal — operator can still add lanes via "+ Add another list".
    }
  }

  return created
}

/** Refresh the picker + rail board caches. Uses allSettled so one
 *  failing invalidation never blocks the other (or the create result). */
export async function invalidateBoardCaches(qc: QueryClient): Promise<void> {
  await Promise.allSettled(
    buildBoardCreateInvalidationKeys().map((queryKey) =>
      qc.invalidateQueries({ queryKey }),
    ),
  )
}

export interface UseCreateBoardFromPickerResult {
  /** Create a board from the dialog's `CreateBoardInput`, refresh the
   *  board caches, and resolve to the new board as an
   *  `AllocateMenuBoard` so the caller can drill into its lane step.
   *  Throws on the primary createBoard failure so the dialog can show
   *  the error inline (secondary clone/provision steps are swallowed). */
  createBoard: (input: CreateBoardInput) => Promise<AllocateMenuBoard>
}

/**
 * React hook binding {@link runBoardCreate} to the shared react-query
 * cache. Returned `createBoard` is stable across renders.
 */
export function useCreateBoardFromPicker(args: {
  api: KanbanApi
}): UseCreateBoardFromPickerResult {
  const { api } = args
  const qc = useQueryClient()

  const createBoard = useCallback(
    async (input: CreateBoardInput): Promise<AllocateMenuBoard> => {
      const board = await runBoardCreate(api, input)
      await invalidateBoardCaches(qc)
      return toAllocateMenuBoard(board)
    },
    [api, qc],
  )

  return { createBoard }
}
